import { useTranslate } from '@refinedev/core';
import { Flex, Space, Tag, Typography, theme } from 'antd';
import { StarFilled } from '@ant-design/icons';
import type { PublicationDetailOut } from '@/api/generated/schemas';
import { LabelKind } from '@/api/generated/schemas';
import { StatCell } from '@/components/common/show/StatCell';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { orDash } from '@/components/common/show/shared';
import { CoverThumb } from '@/components/common/show/CoverThumb';
import { ExclusivityTag } from '@/components/user_catalog/publications/ExclusivityTag';
import { formatRating, formatThousands } from '@/utils/format';
import { availabilityStatusColors, labelColors } from './publicationColors';

const { Text, Title } = Typography;

interface PublicationHeroProps {
  record: PublicationDetailOut;
  /** Smaller cover, title and stats — for the quick-view drawer. */
  compact?: boolean;
}

/** Publication identity block: cover, title with status tags, creators line
 * and headline stats. Shared by the show header and the quick-view drawer. */
export const PublicationHero = ({ record, compact }: PublicationHeroProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const statSize = compact ? undefined : 'lg';

  const personNames = (
    items: { position: number; personId: number; name?: string }[] | undefined
  ) =>
    (items ?? [])
      .slice()
      .sort((a, b) => a.position - b.position)
      .map(({ personId, name }) => name ?? `#${personId}`)
      .join(', ');

  const authorNames = personNames(
    record.publication_authors?.map((a) => ({
      position: a.position,
      personId: a.author_id,
      name: a.author?.name,
    }))
  );
  const lectorNames = personNames(
    record.publication_lectors?.map((l) => ({
      position: l.position,
      personId: l.lector_id,
      name: l.lector?.name,
    }))
  );

  const subtitleLine = [
    record.subtitle,
    authorNames,
    lectorNames &&
      t('user_catalog.publications.show.hero.read_by', { names: lectorNames }),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Flex gap={compact ? token.margin : token.marginLG} align="flex-start" wrap>
      <CoverThumb url={record.cover_url} size={compact ? 96 : 128} />

      <div style={{ flex: 1, minWidth: compact ? 220 : 260 }}>
        <Flex gap={token.marginSM} align="center" wrap>
          <Title
            level={compact ? 4 : 3}
            style={{
              margin: 0,
              fontSize: compact
                ? token.fontSizeHeading4
                : token.fontSizeHeading3,
            }}
          >
            {record.title}
          </Title>
          {(record.all_labels ?? []).map((label) => (
            <Tag key={label} color={labelColors[label] ?? 'default'}>
              {t(`base.labels.${label}`)}
            </Tag>
          ))}
          {/* The `exclusive` label and `is_exclusive` are separate
              server derivations; show the window whenever the label
              alone would hide it, without doubling up. */}
          {!record.all_labels?.includes(LabelKind.exclusive) && (
            <ExclusivityTag
              isExclusive={record.is_exclusive}
              ranges={record.exclusive_ranges}
            />
          )}
          {record.availability_status && (
            <Tag
              color={
                availabilityStatusColors[record.availability_status] ??
                'default'
              }
            >
              {t(
                `user_catalog.publications.availability_status.${record.availability_status}`
              )}
            </Tag>
          )}
        </Flex>
        {subtitleLine && (
          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {subtitleLine}
          </Text>
        )}

        <Flex
          gap={compact ? token.marginLG : token.marginXL + token.margin}
          style={{ marginTop: compact ? token.marginSM : token.marginMD }}
          wrap
        >
          <StatCell
            size={statSize}
            label={t('user_catalog.publications.fields.total_rating')}
          >
            {record.total_rating != null ? (
              <Space size={6}>
                <StarFilled style={{ color: token.yellow }} />
                {formatRating(record.total_rating)}
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  (
                  {t('user_catalog.publications.show.hero.reviews_count', {
                    count: record.reviews_count ?? 0,
                  })}
                  )
                </Text>
              </Space>
            ) : (
              orDash(null)
            )}
          </StatCell>
          <StatCell
            size={statSize}
            label={t('user_catalog.publications.fields.popularity')}
          >
            {formatThousands(record.popularity ?? 0)}
          </StatCell>
          <StatCell size={statSize} label={t('base.fields.mod_status')}>
            <ModStatusTag value={record.mod_status} />
          </StatCell>
          <StatCell
            size={statSize}
            label={t('user_catalog.publications.fields.formats')}
          >
            {record.ebook || record.audio ? (
              <Space size={6}>
                {record.ebook && (
                  <Tag color="cyan">
                    {t('user_catalog.publications.tabs.ebook')}
                  </Tag>
                )}
                {record.audio && (
                  <Tag color="orange">
                    {t('user_catalog.publications.tabs.audio')}
                  </Tag>
                )}
              </Space>
            ) : (
              orDash(null)
            )}
          </StatCell>
        </Flex>
      </div>
    </Flex>
  );
};
